import React, { useState, useRef, useEffect } from "react"
import * as Yup from "yup"
import { useFormik } from "formik"
import { Link, useNavigate } from "react-router-dom"
import { Alert, Card, CardBody, Col, Container, Form, FormFeedback, Input, Label, Row } from "reactstrap"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"

import logoDark from "../../../assets/images/pdflogo.png"
import logoLight from "../../../assets/images/pdflogo.png"
import { loginSuccess, apiError } from "store/actions"
import { sendOtpApi, registerApi, getPostLoginRedirectPath } from "helpers/auth_api"

type RegisterStep = "form" | "otp"
const OTP_LENGTH = 6

const Register = (props: any) => {
  document.title = "Register | SoldX"

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [step, setStep] = useState<RegisterStep>("form")
  const [loading, setLoading] = useState(false)
  const [otp, setOtp] = useState("")
  const [otpError, setOtpError] = useState("")
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  const selectLoginState = (state: any) => state.Login
  const { error } = useSelector(createSelector(selectLoginState, (login) => ({ error: login.error })))

  const setAuthUserAndRedirect = (res: { data: any; token: string }) => {
    const d = res.data
    const authUser = {
      ...d,
      uid: d._id,
      username: d.fullName,
      accessToken: res.token,
      token: res.token,
      profileImageUrl:
        d.profileImageUrl ??
        d.profile_image_url ??
        d.avatar ??
        d.profileImage ??
        (d.profile && (d.profile.profileImageUrl ?? d.profile.avatar ?? d.profile.imageUrl)),
    }
    localStorage.setItem("authUser", JSON.stringify(authUser))
    dispatch(loginSuccess(authUser))
    navigate(getPostLoginRedirectPath(d.role))
  }

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      fullName: "",
      email: "",
      type: "Buyer" as "Buyer" | "Seller" | "Broker",
      password: "",
    },
    validationSchema: Yup.object({
      fullName: Yup.string().required("Please enter your full name").trim(),
      email: Yup.string().required("Please enter your email").email("Invalid email"),
      type: Yup.string().oneOf(["Buyer", "Seller", "Broker"]).required("Please select type"),
      password: Yup.string().required("Please enter your password").min(6, "Password must be at least 6 characters"),
    }),
    onSubmit: async (values: { fullName: string; email: string; type: "Buyer" | "Seller" | "Broker"; password: string }) => {
      if (step === "form") {
        setLoading(true)
        dispatch(apiError(""))
        try {
          const res = await sendOtpApi({ email: values.email, purpose: "register" })
          if (res.success) {
            setStep("otp")
            setOtpError("")
          } else {
            dispatch(apiError(res.message || "Failed to send OTP"))
          }
        } catch (err: any) {
          const msg =
            err?.response?.data?.message || err?.response?.data?.error || err?.message || "Failed to send OTP"
          dispatch(apiError(typeof msg === "string" ? msg : "Failed to send OTP"))
        } finally {
          setLoading(false)
        }
      }
    },
  })

  useEffect(() => {
    if (step === "otp") otpInputRefs.current[0]?.focus()
  }, [step])

  const otpDigits = otp.padEnd(OTP_LENGTH, " ").split("").slice(0, OTP_LENGTH)

  const handleOtpChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, "").slice(-1)
    const next = otp.slice(0, index) + digit + otp.slice(index + 1)
    const newOtp = next.slice(0, OTP_LENGTH).replace(/\D/g, "")
    setOtp(newOtp)
    setOtpError("")
    if (digit && index < OTP_LENGTH - 1) otpInputRefs.current[index + 1]?.focus()
  }

  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
      setOtp(otp.slice(0, index - 1))
    }
  }

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, OTP_LENGTH)
    if (pasted.length > 0) {
      setOtp(pasted)
      setOtpError("")
      const nextIndex = Math.min(pasted.length, OTP_LENGTH - 1)
      otpInputRefs.current[nextIndex]?.focus()
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setOtpError("")
    if (otp.length !== OTP_LENGTH) {
      setOtpError("Please enter the 6-digit OTP sent to your email")
      return
    }
    setLoading(true)
    dispatch(apiError(""))
    try {
      const res = await registerApi({
        fullName: validation.values.fullName,
        email: validation.values.email,
        type: validation.values.type,
        password: validation.values.password,
        otp: otp.trim(),
      })
      if (res.success && res.data && res.token) {
        setAuthUserAndRedirect(res)
      } else {
        setOtpError(res.message || "Registration failed")
      }
    } catch (err: any) {
      const msg =
        err?.response?.data?.message || err?.response?.data?.error || err?.message || "Registration failed"
      setOtpError(typeof msg === "string" ? msg : "Registration failed")
    } finally {
      setLoading(false)
    }
  }

  const handleBackToForm = () => {
    setStep("form")
    setOtp("")
    setOtpError("")
  }

  return (
    <React.Fragment>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="overflow-hidden">
                <CardBody className="pt-0">
                  <h3 className="text-center mt-5 mb-4">
                    <Link to="/" className="d-block auth-logo">
                      <img src={logoDark} alt="" height="30" className="auth-logo-dark" />
                      <img src={logoLight} alt="" height="30" className="auth-logo-light" />
                    </Link>
                  </h3>
                  <div className="p-3">
                    {step === "form" ? (
                      <>
                        <h4 className="text-muted font-size-18 mb-1 text-center">Free Register</h4>
                        <p className="text-muted text-center">Get your free SoldX account now.</p>
                        <Form
                          className="form-horizontal mt-4"
                          onSubmit={(e) => {
                            e.preventDefault()
                            validation.handleSubmit()
                            return false
                          }}
                        >
                          {error ? <Alert color="danger">{error}</Alert> : null}
                          <div className="mb-3">
                            <Label htmlFor="fullName">Full Name</Label>
                            <Input
                              id="fullName"
                              name="fullName"
                              className="form-control"
                              placeholder="Enter full name"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.fullName}
                              invalid={!!(validation.touched.fullName && validation.errors.fullName)}
                            />
                            {validation.touched.fullName && validation.errors.fullName ? (
                              <FormFeedback type="invalid">{validation.errors.fullName}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="email">Email</Label>
                            <Input
                              id="email"
                              name="email"
                              className="form-control"
                              placeholder="Enter email"
                              type="email"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.email}
                              invalid={!!(validation.touched.email && validation.errors.email)}
                            />
                            {validation.touched.email && validation.errors.email ? (
                              <FormFeedback type="invalid">{validation.errors.email}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="type">I am a</Label>
                            <Input
                              id="type"
                              name="type"
                              type="select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.type}
                              invalid={!!(validation.touched.type && validation.errors.type)}
                              className="form-select"
                            >
                              <option value="Buyer">Buyer</option>
                              <option value="Seller">Seller</option>
                              <option value="Broker">Broker</option>
                            </Input>
                            {validation.touched.type && validation.errors.type ? (
                              <FormFeedback type="invalid">{validation.errors.type}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3">
                            <Label htmlFor="password">Password</Label>
                            <Input
                              id="password"
                              name="password"
                              type="password"
                              placeholder="Enter password"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password}
                              invalid={!!(validation.touched.password && validation.errors.password)}
                            />
                            {validation.touched.password && validation.errors.password ? (
                              <FormFeedback type="invalid">{validation.errors.password}</FormFeedback>
                            ) : null}
                          </div>
                          <div className="mb-3 row mt-4">
                            <div className="col-12 text-end">
                              <button
                                className="btn btn-primary w-md waves-effect waves-light"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? "Sending OTP..." : "Send OTP"}
                              </button>
                            </div>
                          </div>
                          <div className="mb-0 row">
                            <div className="col-12 mt-4">
                              <p className="text-muted mb-0 font-size-14">
                                By registering you agree to the SoldX{" "}
                                <Link to="#" className="text-primary">Terms of Use</Link>
                              </p>
                            </div>
                          </div>
                        </Form>
                      </>
                    ) : (
                      <>
                        <h4 className="text-muted font-size-18 mb-1 text-center">Verify OTP</h4>
                        <p className="text-muted text-center">
                          We sent a 6-digit code to <strong>{validation.values.email}</strong>. Enter it below.
                        </p>
                        <Form className="form-horizontal mt-4" onSubmit={handleRegister}>
                          {error ? <Alert color="danger">{error}</Alert> : null}
                          {otpError ? <Alert color="danger">{otpError}</Alert> : null}
                          <div className="mb-3">
                            <Label>Email</Label>
                            <Input
                              type="email"
                              value={validation.values.email}
                              readOnly
                              disabled
                              className="form-control bg-light"
                            />
                          </div>
                          <div className="mb-3">
                            <Label className="d-block text-center mb-3">Enter 6-digit OTP</Label>
                            <div className="d-flex justify-content-center gap-2 flex-wrap" style={{ gap: "0.5rem" }}>
                              {Array.from({ length: OTP_LENGTH }, (_, index) => (
                                <input
                                  key={index}
                                  ref={(el) => { otpInputRefs.current[index] = el }}
                                  type="text"
                                  inputMode="numeric"
                                  maxLength={1}
                                  value={otpDigits[index]?.trim() ?? ""}
                                  onChange={(e) => handleOtpChange(index, e.target.value)}
                                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                  onPaste={handleOtpPaste}
                                  autoComplete="one-time-code"
                                  className="form-control text-center fw-bold"
                                  style={{
                                    width: "2.75rem",
                                    height: "2.75rem",
                                    fontSize: "1.25rem",
                                    padding: "0.25rem",
                                    border: "2px solid #ced4da",
                                    borderRadius: "8px",
                                  }}
                                  aria-label={`Digit ${index + 1}`}
                                />
                              ))}
                            </div>
                          </div>
                          <Row className="mb-3 mt-4">
                            <div className="col-6">
                              <button
                                type="button"
                                className="btn btn-light w-md"
                                onClick={handleBackToForm}
                                disabled={loading}
                              >
                                Back
                              </button>
                            </div>
                            <div className="col-6 text-end">
                              <button
                                className="btn btn-primary w-md waves-effect waves-light"
                                type="submit"
                                disabled={loading}
                              >
                                {loading ? "Registering..." : "Register"}
                              </button>
                            </div>
                          </Row>
                        </Form>
                      </>
                    )}
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  Already have an account? <Link to="/login" className="text-primary">Login</Link>
                </p>
                <p>
                  <Link to="/" className="btn btn-outline-primary btn-sm">
                    <i className="mdi mdi-home me-1"></i>Go to Home
                  </Link>
                </p>
                © {new Date().getFullYear()} SoldX{" "}
                <span className="d-none d-sm-inline-block"> - All Rights Reserved</span>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Register
